import { useEffect, useMemo, useRef, useState } from "react";

// 👉 Same backend URL your Indian live feed uses.
const API_BASE =
  process.env.REACT_APP_LIVE_URL || process.env.REACT_APP_API_BASE_URL;

const norm = (s) =>
  String(s || "")
    .toUpperCase()
    .trim()
    .replace(/[-/\s]/g, ".");

/**
 * Live US prices — same protocol as your Indian /live/feed:
 * open SSE → receive userId → POST symbols → receive "tick" events.
 *
 *   const { priceOf, live, flash } = useGlobalLiveFeed(allRows.map((r) => r.symbol));
 *   const ltp = priceOf(row.symbol)?.ltp ?? row.ltp;
 *
 * Pass ALL rows of the selected universe (not the filtered ones) so switching
 * Daily/Weekly or Bullish/Bearish doesn't change the subscription.
 * Changing the universe (NASDAQ 100 ↔ S&P 500) just re-POSTs the symbols —
 * the SSE connection stays open.
 */
export function useGlobalLiveFeed(symbols) {
  const key = useMemo(
    () =>
      [...new Set((symbols || []).map(norm).filter(Boolean))].sort().join(","),
    [symbols],
  );

  const [ticks, setTicks] = useState({}); // symbol → tick
  const [live, setLive] = useState(false);
  const [flash, setFlash] = useState({}); // symbol → "up" | "down"

  const userIdRef = useRef(null);
  const keyRef = useRef(key);
  const pending = useRef({}); // ticks buffered between renders
  const lastLtp = useRef({});

  const postSymbols = (userId, k) =>
    fetch(`${API_BASE}/api/global-live/symbols`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, symbols: k ? k.split(",") : [] }),
    }).catch(() => {});

  // One SSE connection for the life of the page
  useEffect(() => {
    const es = new EventSource(`${API_BASE}/api/global-live/feed`);

    es.addEventListener("connected", (e) => {
      const { userId } = JSON.parse(e.data);
      userIdRef.current = userId; // new id after every auto-reconnect → re-send symbols
      if (keyRef.current) postSymbols(userId, keyRef.current);
    });
    es.addEventListener("status", (e) =>
      setLive(Boolean(JSON.parse(e.data).live)),
    );
    es.addEventListener("snapshot", (e) =>
      Object.assign(pending.current, JSON.parse(e.data)),
    );
    es.addEventListener("tick", (e) => {
      const t = JSON.parse(e.data);
      pending.current[t.symbol] = t;
    });
    es.onerror = () => setLive(false); // EventSource reconnects by itself

    // Render at most twice a second, however many ticks arrive
    const flush = setInterval(() => {
      const batch = pending.current;
      const syms = Object.keys(batch);
      if (!syms.length) return;
      pending.current = {};

      const changed = {};
      for (const s of syms) {
        const prev = lastLtp.current[s];
        if (prev != null && batch[s].ltp !== prev)
          changed[s] = batch[s].ltp > prev ? "up" : "down";
        lastLtp.current[s] = batch[s].ltp;
      }

      setTicks((old) => ({ ...old, ...batch }));
      if (Object.keys(changed).length) {
        setFlash(changed);
        setTimeout(() => setFlash({}), 700);
      }
    }, 500);

    return () => {
      clearInterval(flush);
      es.close();
    };
  }, []);

  // Symbols changed (e.g. NASDAQ 100 → S&P 500): just re-POST
  useEffect(() => {
    keyRef.current = key;
    if (userIdRef.current) postSymbols(userIdRef.current, key);
  }, [key]);

  const priceOf = (symbol) => ticks[norm(symbol)];
  const flashOf = (symbol) => flash[norm(symbol)];

  return { ticks, priceOf, flashOf, live };
}

/**
 * Live SL / target hint for a crossover row.
 * Bullish: SL below, target above. Bearish: SL above, target below.
 * Returns "sl_hit" | "target_hit" | "open" | null (no live price).
 * 👉 Rename row.crossover / row.sl / row.target if your API uses other names.
 */
export function liveCrossoverStatus(row, ltp) {
  if (ltp == null || row.sl == null || row.target == null) return null;
  const bullish = String(row.crossover).toLowerCase().includes("bull");
  if (bullish) {
    if (ltp <= row.sl) return "sl_hit";
    if (ltp >= row.target) return "target_hit";
  } else {
    if (ltp >= row.sl) return "sl_hit";
    if (ltp <= row.target) return "target_hit";
  }
  return "open";
}
